import { isArray, isEmpty, isNumber, isObject } from 'radash';
import { MediaContent, MediaContentResponse, ThumbnailDetails } from '@goldcast/api/content';
import { getFallbackFPS } from './fpsUtils';
import { isLayoutAudiogram, isLayoutDefault, isSpeakerLayout } from './layout';
import {
  getContentBroadcastId,
  getContentFPS,
  isAlignedStreamAvailable,
  isAudioContent,
  isContentCompositeMp4Available,
  isEventRecording,
  isEventTypeRecording,
  isLiveEventRecording,
  isLiveEventWithManualMigration,
  isPreRecordedEvent,
  isRemotionMigrationDone,
  isRemotionMigrationInProgress,
  isRemotionMigrationNotStarted,
  isRemotionMigrationPossibleForContent,
  isUploadedVideo
} from './mediaContentUtils';
import { LayoutDisableReason, FitType } from '../types/asset-types';
import { Clip } from '../types/clip-types';
import { CompositionConfig } from '../types/RemotionPlayerConfigTypes/CompositionConfig';
import { CompositeSegment } from '../types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { SegmentType } from '../types/Segment/Type';
import { getSegmentsTrack } from './playerConfigGetters';
import { Segment } from '../types/RemotionPlayerConfigTypes/Segment/Segment';

export const isClipAudiogram = (clip: Clip): boolean => {
  return isLayoutAudiogram(clip?.asset_metadata?.layout);
};

export const isClipFromRecordingStudio = (clip: Clip) => {
  return isEventTypeRecording(clip?.content?.event_type);
};

/** To check if recording is Full Recording (Not a clip) */
export const isFullLengthClip = (clip: Clip): boolean => {
  return clip.asset_metadata.is_full_length;
};

/** To check if Recording is a clip */
export const isNotFullLengthClip = (clip: Clip) => {
  return !isFullLengthClip(clip);
};

/** To check if recording is a duplicate of another full-length recording */
export const getIsDuplicateRecording = (clip: Clip): boolean => {
  return !!clip?.asset_metadata?.duplicated_from || !!clip?.duplicated_from;
};

export const getClipTranscriptTitle = (clip: Clip, isClipDuplicateEnabled: boolean): string => {
  if (getIsDuplicateRecording(clip)) {
    return clip.title;
  }
  if (isFullLengthClip(clip)) {
    if (isClipDuplicateEnabled) {
      return 'Original';
    }
    return 'Recording';
  }
  return 'Clip';
};

export const isClipFromEvents = (clip: Clip): boolean => {
  return isEventRecording(clip?.content?.media_source_type, clip?.content?.event_type);
};

export const isClipFromUploads = (clip: Clip): boolean => {
  return isUploadedVideo(clip?.content?.media_source_type);
};

export const shouldShowLowResolutionPreview = (clip: Clip): boolean => {
  return !isClipAudiogram(clip);
};

export const isClipLayoutDefault = (clip: Clip): boolean => {
  return isLayoutDefault(clip?.asset_metadata?.layout);
};

export const isClipInOriginalLanguage = (clip: Clip): boolean => {
  if (!clip.asset_metadata?.selected_lang) {
    return true;
  }
  return clip.asset_metadata?.selected_lang === clip.asset_metadata?.original_lang;
};

export const getClipVersion = (): number => {
  return Date.now();
};

export const getClipDurationInSeconds = (clip: Clip): number => {
  const durationinFrames = clip.asset_metadata?.player_config?.durationInFrames;
  if (durationinFrames) {
    return durationinFrames / getClipFPS(clip);
  }
  const { duration, introDuration, outroDuration, start, end } = clip.asset_metadata ?? {};
  let finalDuration = 0;
  if (isNumber(duration)) {
    finalDuration += duration;
    if (isNumber(introDuration)) {
      finalDuration += introDuration;
    }
    if (isNumber(outroDuration)) {
      finalDuration += outroDuration;
    }
  } else {
    finalDuration += end - start;
  }
  return finalDuration;
};

export const getClonedPlayerConfigFromClip = (clip: any): CompositionConfig | null => {
  if (!clip?.asset_metadata?.player_config) {
    return null;
  }
  return structuredClone(clip.asset_metadata.player_config);
};

export const getClipReadOnlyPlayerConfig = (clip: Clip): CompositionConfig | null => {
  if (isClipInvalid(clip)) {
    return null;
  }
  if (!clip?.asset_metadata?.player_config) {
    return null;
  }
  return clip.asset_metadata.player_config;
};

export const doesClipHavePlayerConfig = (clip: Clip): boolean => {
  return isObject(getClipReadOnlyPlayerConfig(clip)) && !isEmpty(getClipReadOnlyPlayerConfig(clip));
};

export const isClipInvalid = (clip: Clip): boolean => {
  return !isObject(clip) || isEmpty(clip);
};

export const getClipFPS = (clip: Clip): number => {
  if (isClipInvalid(clip)) {
    return getFallbackFPS();
  }

  const playerConfig = clip.asset_metadata.player_config;
  if (playerConfig !== null && isNumber(playerConfig.fps)) {
    return playerConfig.fps;
  }

  return getContentFPS(clip.content);
};

export const doesClipHaveScenes = (clip: Clip): boolean => {
  return isArray(clip.asset_metadata.scenes) && clip.asset_metadata.scenes.length > 0;
};

export const doesClipHaveMultipleScenes = (clip: Clip): boolean => {
  return doesClipHaveScenes(clip) && isArray(clip.asset_metadata.scenes) && clip.asset_metadata.scenes.length > 1;
};

export const getClipScenes = (clip: Clip) => {
  return clip.asset_metadata.scenes || [];
};

export const isClipLiveEvent = (clip: Clip): boolean => {
  return isLiveEventRecording(clip.content?.media_source_type, clip.content?.event_type, clip.content?.broadcast_type);
};

export const isClipPreRecordedEvent = (clip: Clip): boolean => {
  return isPreRecordedEvent(clip.content?.media_source_type, clip.content?.event_type, clip.content?.broadcast_type);
};

export const isClipAlignedStreamAvailable = (clip: Clip): boolean => {
  return isAlignedStreamAvailable(clip.content.individual_recording_status);
};

export const isClipLiveEventAndRemotionMigrationNotStarted = (clip: Clip): boolean => {
  return isClipLiveEvent(clip) && isRemotionMigrationNotStarted(clip.content);
};

export const isClipLiveEventAndRemotionMigrationInProgress = (clip: Clip): boolean => {
  return isClipLiveEvent(clip) && isRemotionMigrationInProgress(clip.content);
};

export const isClipLiveEventAndRemotionMigrationDone = (clip: Clip): boolean => {
  return isClipLiveEvent(clip) && isRemotionMigrationDone(clip.content);
};

// Going forward this function needs update for Splits V2. As content id will be different for different splits segment.
export const getClipBroadcastId = (clip: Clip) => {
  if (isClipInvalid(clip)) {
    return null;
  }
  return getContentBroadcastId(clip.content);
};

export const getClipFitMode = (clip: Clip): FitType => {
  if (isClipInvalid(clip)) {
    return 'FIT';
  }
  return clip.asset_metadata.fit;
};

export const getClipFitFillDisabledReason = (clip: Clip): string | null => {
  if (isClipInvalid(clip)) {
    return null;
  }
  if (isClipAudiogram(clip)) {
    return LayoutDisableReason.FIT_FILL_NOT_SUPPORTED_FOR_AUDIOGRAM;
  }
  if (!doesClipHaveMultipleScenes(clip) && isLayoutDefault(clip.asset_metadata.layout)) {
    return LayoutDisableReason.FILL_NOT_SUPPORTED_FOR_ORIGINAL_LAYOUT;
  }
  if (
    !doesClipHaveMultipleScenes(clip) &&
    isSpeakerLayout(clip.asset_metadata.layout) &&
    (isClipFromUploads(clip) || isClipPreRecordedEvent(clip))
  ) {
    return LayoutDisableReason.FIT_NOT_SUPPORTED_FOR_UPLOADS_WITH_SPEAKER_LAYOUT;
  }
  return null;
};

export const isFitAppliedForClip = (clip: Clip): boolean => {
  const disableReason = getClipFitFillDisabledReason(clip);
  if (disableReason === LayoutDisableReason.FILL_NOT_SUPPORTED_FOR_ORIGINAL_LAYOUT) {
    return true;
  }
  if (disableReason === LayoutDisableReason.FIT_NOT_SUPPORTED_FOR_UPLOADS_WITH_SPEAKER_LAYOUT) {
    return false;
  }
  return getClipFitMode(clip) === 'FIT';
};

export const isRemotionMigrationPossibleForClip = (clip: Clip): boolean => {
  if (isClipInvalid(clip)) {
    return false;
  }
  return isRemotionMigrationPossibleForContent(clip.content);
};

/**
 * Return true if we've mp4 available (be it preview or source)
 * Which is mandatory requirement for remotion usage
 * @param clip
 * @returns boolean
 */
export const isClipCompositeMp4Available = (clip: Clip): boolean => {
  if (isClipInvalid(clip)) {
    return false;
  }
  return isContentCompositeMp4Available(clip.content);
};

export const getClipDisplayLabel = (clip: Clip): string => {
  if (isClipInvalid(clip)) {
    return 'Invalid';
  }
  if (isClipLiveEvent(clip)) {
    return 'Live Event';
  }
  if (isClipPreRecordedEvent(clip)) {
    return 'Pre-Recorded Event';
  }
  if (isClipFromRecordingStudio(clip)) {
    return 'Recording Studio';
  }
  if (isClipFromUploads(clip)) {
    return 'Upload';
  }
  return 'Unknown';
};

export const getClipThumbnail = (clip: Clip): ThumbnailDetails | null => {
  return clip.thumbnail_details;
};

export const doesClipHaveThumbnail = (clip: Clip): boolean => {
  const thumbnail = getClipThumbnail(clip);
  return !!thumbnail && !!thumbnail.last_thumbnail_generated_at;
};

export const getClipThumbnailUrl = (clip: Clip): string | null => {
  if (doesClipHaveThumbnail(clip)) {
    return getClipThumbnail(clip)!.thumbnail_url;
  }
  return null;
};

export const isClipAudioContent = (clip: Clip): boolean => {
  return isAudioContent(clip?.content);
};

export const isCustomUploadClip = ({ clip }: { clip: Clip }) => {
  return isClipFromUploads(clip) && isNotFullLengthClip(clip);
};

export const isPreRecordedEventClip = ({ clip }: { clip: Clip }): boolean => {
  return isClipPreRecordedEvent(clip) && isNotFullLengthClip(clip);
};
export const isNewLayoutsMigrated = (clip: Clip): boolean => {
  return !!clip.asset_metadata.is_new_layouts_migrated;
};
export const isOriginalLayoutAvailable = (clip: Clip): boolean => {
  if (!isClipFromRecordingStudio(clip)) {
    return true;
  }
  return false;
};
export const isLiveEventRemotionWithPreviewAndAlignedStream = (clip: Clip) => {
  return isClipLiveEvent(clip) && isClipAlignedStreamAvailable(clip);
};
export const doesClipWorkOnAlignedStreams = (clip: Clip) => {
  return isClipFromRecordingStudio(clip) || isLiveEventRemotionWithPreviewAndAlignedStream(clip);
};
export const isLayoutWithVisionDataEnabledPure = ({
  clip,
  isFullFaceRecEnabled,
  isFullFaceRecForPreRecordedEnabled
}: {
  clip: Clip;
  isFullFaceRecEnabled: boolean;
  isFullFaceRecForPreRecordedEnabled: boolean;
}) => {
  return (
    isCustomUploadClip({ clip }) ||
    (isFullFaceRecEnabled && isClipFromUploads(clip)) ||
    isPreRecordedEventClip({ clip }) ||
    (isFullFaceRecForPreRecordedEnabled && isClipPreRecordedEvent(clip))
  );
}; /**
 * Use new timeline for unmigrated live event
 * @param content
 * @returns boolean
 */

export const canUseNewTimelineForUnmigratedLiveEventContent = (content: MediaContent | MediaContentResponse) => {
  return (
    isLiveEventRecording(content.media_source_type, content.event_type, content.broadcast_type) &&
    !isAlignedStreamAvailable(content.individual_recording_status) &&
    isLiveEventWithManualMigration(content) && // old
    isRemotionMigrationPossibleForContent(content)
  );
};

export const canUseNewTimelineForUnmigratedLiveEvent = (clip: Clip) => {
  return canUseNewTimelineForUnmigratedLiveEventContent(clip.content);
}; /**
 * Use New timeline for live events where migration is impossible
 * @param content
 * @returns
 */

export const canUseNewTimelineForOldLiveEventContent = (content: MediaContent | MediaContentResponse) => {
  return (
    isLiveEventRecording(content.media_source_type, content.event_type, content.broadcast_type) &&
    !isAlignedStreamAvailable(content.individual_recording_status) &&
    !isRemotionMigrationPossibleForContent(content)
  );
};

export const canUseNewTimelineForOldLiveEvent = ({ clip }: { clip: Clip }) => {
  return canUseNewTimelineForOldLiveEventContent(clip.content);
};
/**
 * Gets all composite segments from the player config
 */
export const getAllCompositeSegments = (clip: Clip): CompositeSegment[] => {
  const playerConfig = clip.asset_metadata.player_config;
  if (!playerConfig) {
    return [];
  }

  const segmentsTrack = getSegmentsTrack(playerConfig);
  const compositeSegments = segmentsTrack?.segments?.filter(
    segment => segment.type === SegmentType.COMPOSITE
  ) as CompositeSegment[];

  return compositeSegments || [];
};
export const getAllSegments = (clip: Clip): Segment[] => {
  const playerConfig = clip.asset_metadata.player_config;
  if (!playerConfig) {
    return [];
  }
  const segmentsTrack = getSegmentsTrack(playerConfig);
  return segmentsTrack?.segments || [];
};
/** A clip with more than one source window must go through the splits path even before it has a
 *  `player_config` — which is the only thing `isSplitsInTimelineEnabledPure` derives from, so it
 *  returns `false` for a fresh reel. Deliberately does not check `clip_type`. */
export const hasMultipleClipSegments = (clip: Clip): boolean => (clip.asset_metadata.clip_segments?.length ?? 0) > 1;
export const isSplitsInTimelineEnabledPure = ({
  clip,
  isSplitsTranslationEnabled
}: {
  clip: Clip;
  isSplitsTranslationEnabled: boolean;
}) => {
  const originalLang = clip.asset_metadata.original_lang;
  const selectedLang = clip.asset_metadata.selected_lang;
  if (originalLang && selectedLang && originalLang !== selectedLang && !isSplitsTranslationEnabled) {
    return false;
  }
  const allSegments = getAllSegments(clip);
  const hasMultipleCompositeSegments = allSegments.filter(segment => segment.type === SegmentType.COMPOSITE).length > 1;
  if (hasMultipleCompositeSegments) {
    return true;
  }

  const hasNonIntroOutroVideoSegment =
    allSegments.filter(
      segment => segment.type === SegmentType.VIDEO && segment.id !== 'intro' && segment.id !== 'outro'
    ).length > 0;
  if (hasNonIntroOutroVideoSegment) {
    return true;
  }

  return false;
};
export const areAllCompositeInTheSameLanguage = (clip: Clip): boolean => {
  const compositeSegments = getAllCompositeSegments(clip);
  if (compositeSegments.length === 0) {
    return true;
  }
  const selectedLang = clip.asset_metadata.selected_lang;
  if (selectedLang === null || selectedLang === undefined) {
    return true;
  }
  if (selectedLang === clip.asset_metadata.original_lang) {
    return true;
  }
  return compositeSegments.every(segment => segment.segment_metadata?.captions_lang === selectedLang);
};
export const isPlayerConfigStale = ({
  clip,
  logEvent
}: {
  clip: Clip;
  logEvent: (clip: Clip, message: string) => void;
}) => {
  const compositeSegments = getAllCompositeSegments(clip);
  if (!compositeSegments || compositeSegments.length === 0) {
    return false;
  }

  let isStale = false;

  if (isOriginalLayoutAvailable(clip)) {
    const originalVideoPresent = compositeSegments?.[0]?.videos?.find(video => video.isOriginalVideo);

    if (!originalVideoPresent) {
      logEvent(clip, 'ORIGINAL_VIDEO_NOT_PRESENT');
      isStale = true;
    } else if (originalVideoPresent.details.preview !== clip.content.preview) {
      logEvent(clip, 'ORIGINAL_VIDEO_PREVIEW_CHANGED');
      isStale = true;
    }
  }

  if (!doesClipWorkOnAlignedStreams(clip) && isClipAudiogram(clip)) {
    const moreThanOneVideo = compositeSegments.some(compositeSegment => compositeSegment.videos.length > 1);
    const mutedVideoIsHydrated = compositeSegments.some(compositeSegment =>
      compositeSegment.layoutChanges.some(layout => layout?.mutedVideosId?.length > 0)
    );
    if (moreThanOneVideo && !mutedVideoIsHydrated) {
      isStale = true;
    }
  }

  if (!compositeSegments.some(compositeSegment => compositeSegment.playbackAdjustedScenes)) {
    isStale = true;
  }

  return isStale;
};
export const isConfigGenerationNeeded = ({
  clip,
  isSharedPage,
  hasInitializedMaxVideoDuration,
  hasUpdatedIntroOutroDurations,
  hasMigratedClip,
  logEvent,
  useClInitNewPlayerConfig,
  isSplitsTranslationEnabled
}: {
  clip: Clip;
  isSharedPage: boolean;
  hasInitializedMaxVideoDuration: boolean;
  hasUpdatedIntroOutroDurations: boolean;
  hasMigratedClip: boolean;
  logEvent: (clip: Clip, message: string) => void;
  useClInitNewPlayerConfig: boolean;
  isSplitsTranslationEnabled: boolean;
}) => {
  if (isSharedPage) {
    return false;
  }
  const isPlayerConfigNeededAndMissing = !doesClipHavePlayerConfig(clip);
  if (isPlayerConfigNeededAndMissing) {
    return true;
  }

  const isSplitsEnabled = isSplitsInTimelineEnabledPure({ clip, isSplitsTranslationEnabled });
  const allCompositeSegments = getAllCompositeSegments(clip);
  const allCompositeHaveSegmentMetadata = allCompositeSegments.every(
    compositeSegment => compositeSegment.segment_metadata
  );
  if (isSplitsEnabled && !allCompositeHaveSegmentMetadata) {
    return true;
  }
  if (!isSplitsEnabled && allCompositeHaveSegmentMetadata) {
    return true;
  }

  const allCompositeSegmentsInTheSameLang = areAllCompositeInTheSameLanguage(clip);
  if (isSplitsEnabled && !allCompositeSegmentsInTheSameLang) {
    return true;
  }

  if (useClInitNewPlayerConfig) {
    return true;
  }

  if (hasInitializedMaxVideoDuration && !isSharedPage) {
    return true;
  }

  if (hasUpdatedIntroOutroDurations && !isSharedPage) {
    return true;
  }

  if (isPlayerConfigStale({ clip, logEvent })) {
    return true;
  }

  if (hasMigratedClip) {
    return true;
  }

  return false;
};
/**
 * Gets a specific composite segment by ID
 */
export const getCompositeSegmentById = (clip: Clip, compositeId: string): CompositeSegment => {
  const compositeSegments = getAllCompositeSegments(clip);

  if (compositeSegments.length === 0) {
    throw new Error('No composite segments found in player config');
  }

  const segment = compositeSegments.find(seg => seg.id === compositeId);
  if (!segment) {
    throw new Error(`Composite segment with id "${compositeId}" not found`);
  }

  return segment;
};
export const getStartForSegment = ({
  clip,
  segmentId,
  isSplitsTranslationEnabled
}: {
  clip: Clip;
  segmentId?: string;
  isSplitsTranslationEnabled: boolean;
}) => {
  // A highlight reel with multiple source windows is always treated as a splits clip — checked before
  // the translation guard so translated reels still go through the splits path.
  const splitsEnabled =
    isSplitsInTimelineEnabledPure({ clip, isSplitsTranslationEnabled }) || hasMultipleClipSegments(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    return clip.asset_metadata.start;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getCompositeSegmentById(clip, segmentId);
    return segment.segment_metadata?.start || 0;
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.start || 0;
};
export const getEndForSegment = ({
  clip,
  segmentId,
  isSplitsTranslationEnabled
}: {
  clip: Clip;
  segmentId?: string;
  isSplitsTranslationEnabled: boolean;
}) => {
  // A highlight reel with multiple source windows is always treated as a splits clip — checked before
  // the translation guard so translated reels still go through the splits path.
  const splitsEnabled =
    isSplitsInTimelineEnabledPure({ clip, isSplitsTranslationEnabled }) || hasMultipleClipSegments(clip);
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    return clip.asset_metadata.end;
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const allSegments = getAllSegments(clip);
    const segment = allSegments.find(segment => segment.id === segmentId);
    if (!segment) {
      throw new Error('Segment not found');
    }
    if (segment.type === SegmentType.VIDEO) {
      return (segment.toFrame - segment.fromFrame) / getClipFPS(clip);
    }
    if (segment.type === SegmentType.COMPOSITE) {
      return segment.currentBounds?.toFrame / getClipFPS(clip);
    }
    return 0;
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.end || 0;
};
export const shouldFetchSceneOverrides = ({
  clip,
  isSplitsTranslationEnabled
}: {
  clip: Clip;
  isSplitsTranslationEnabled: boolean;
}) => {
  const isSplitsEnabled = isSplitsInTimelineEnabledPure({ clip, isSplitsTranslationEnabled });
  if (isSplitsEnabled) {
    return false;
  }
  if (clip.asset_metadata.scenes?.length && clip.asset_metadata.scenes.length > 0) {
    return false;
  }

  return true;
};
export function normalizeClipConfigForDeleteFlag<T extends Partial<Clip>>(clipData: T): T {
  if (!clipData?.asset_metadata) {
    return clipData;
  }

  return {
    ...clipData,
    asset_metadata: {
      ...clipData.asset_metadata,
      config: null
    }
  };
}
